import type { Api } from '@gleamer/types';

type PredictionApi = Api.PredictionApi;
type DetectionApi = Api.DetectionApi;

/** The detection `type` of a Pre-annotation: a contour, not a box. */
export const PRE_ANNOTATION_DETECTION_TYPE = 'BAC pre-annotation';

/** A dropped DICOM instance the AI team's JSON may refer to. */
export type DroppedView = {
  studyInstanceUID: string;
  seriesInstanceUID: string;
  sopInstanceUID: string;
  numberOfFrames: number;
  /** The plane of a frame, numbered from 1 like DICOM frames. */
  getFrameGeometry: (frameNumber: number) => FrameGeometry | undefined;
};

/** A frame's plane, as in ImagePositionPatient, ImageOrientationPatient and PixelSpacing. */
export type FrameGeometry = {
  imagePositionPatient: number[];
  /** Row direction cosines (increasing x), then column direction cosines (increasing y). */
  imageOrientationPatient: number[];
  /** Spacing between rows (along y), then between columns (along x), in mm. */
  pixelSpacing: number[];
};

type Point = DetectionApi['points'][number];

type Vertex = [number, number];

type BacCandidate = {
  dilated_contour?: { uid?: string; extra_info?: { coords_native?: Vertex[] } };
  patch_prob?: number;
};

type BacViewResult = {
  roi_candidates?: Record<string, BacCandidate[]>;
};

type BacResult = {
  results?: { view_results?: Record<string, BacViewResult> };
};

/**
 * The AI team's slice key n is a 0-based index into the DBT pixel array, whose
 * order matches the DICOM frames: it is DICOM frame n + 1. Not confirmed by the
 * AI team yet (see pre-annotation-demo-plan.md): change it here only.
 */
export function sliceKeyToFrameNumber(sliceKey: string): number {
  return Number(sliceKey) + 1;
}

/**
 * The drawn outline: the shipped `dilated_contour`, as an open ring (the
 * drawing closes it). A ring that repeats its first vertex loses the copy.
 */
function getOutline(candidate: BacCandidate): Vertex[] {
  const vertices = candidate.dilated_contour?.extra_info?.coords_native ?? [];
  const [first] = vertices;
  const last = vertices[vertices.length - 1];
  const isClosed =
    vertices.length > 2 && first[0] === last[0] && first[1] === last[1];
  return isClosed ? vertices.slice(0, -1) : vertices;
}

function toPatientPoint([x, y]: Vertex, frame: FrameGeometry): Point {
  const [rowSpacing, columnSpacing] = frame.pixelSpacing;
  const rowCosines = frame.imageOrientationPatient.slice(0, 3);
  const columnCosines = frame.imageOrientationPatient.slice(3, 6);
  const [px, py, pz] = [0, 1, 2].map(
    (axis) =>
      frame.imagePositionPatient[axis] +
      x * columnSpacing * rowCosines[axis] +
      y * rowSpacing * columnCosines[axis]
  );
  return { x: px, y: py, z: pz };
}

/**
 * Converts the AI team's `DH_BAC_semantic_result.json` into one Prediction per
 * dropped view, with one Detection per Pre-annotation (see CONTEXT.md).
 */
export function bacResultToPredictions(
  result: unknown,
  findView: (sopInstanceUID: string) => DroppedView | undefined
): PredictionApi[] {
  const viewResults = (result as BacResult)?.results?.view_results ?? {};

  return Object.entries(viewResults).flatMap(([sopInstanceUID, viewResult]) => {
    // Views of the JSON without a DICOM in the drop are ignored.
    const view = findView(sopInstanceUID);
    if (!view) {
      return [];
    }
    // A multi-frame DBT goes through the 3D path (patient-space points, no
    // sopInstanceUID); a single-frame FFDM through the 2D path (pixel points).
    const isVolume = view.numberOfFrames > 1;

    const detections: DetectionApi[] = Object.entries(
      viewResult.roi_candidates ?? {}
    ).flatMap(([sliceKey, candidates]) => {
      const frame = isVolume
        ? view.getFrameGeometry(sliceKeyToFrameNumber(sliceKey))
        : undefined;
      if (isVolume && !frame) {
        return [];
      }
      const toPoint = (vertex: Vertex): Point =>
        frame
          ? toPatientPoint(vertex, frame)
          : { x: vertex[0], y: vertex[1], z: 0 };

      // Every outline with at least two points is a Pre-annotation, whatever
      // its score (README of the AI team).
      return candidates
        .map((candidate) => ({ candidate, outline: getOutline(candidate) }))
        .filter(({ outline }) => outline.length >= 2)
        .map(({ candidate, outline }) => ({
          id: candidate.dilated_contour.uid,
          type: PRE_ANNOTATION_DETECTION_TYPE,
          confidence: (candidate.patch_prob ?? 0) * 100,
          points: outline.map(toPoint),
        }));
    });
    if (!detections.length) {
      return [];
    }

    const prediction: PredictionApi = {
      studyInstanceUID: view.studyInstanceUID,
      seriesInstanceUID: view.seriesInstanceUID,
      detections,
    };
    if (!isVolume) {
      prediction.sopInstanceUID = view.sopInstanceUID;
    }
    return [prediction];
  });
}
